import type { AuthUser } from './index';

/** Roles at or below this level are considered admin roles. */
export const ADMIN_ROLE_LEVEL_THRESHOLD = 4;

/** Returns `true` if the user holds any admin-level role (level <= 4) or is a platform super user. */
export function isAdminUser(user: Pick<AuthUser, 'is_superuser' | 'roles'>): boolean {
  return user.is_superuser || user.roles.some((r) => r.role_level <= ADMIN_ROLE_LEVEL_THRESHOLD);
}

/** Returns `true` if the user is a platform super user or holds the Company Super Admin role. */
export function isSuperAdminUser(user: Pick<AuthUser, 'is_superuser' | 'roles'>): boolean {
  return user.is_superuser || user.roles?.some((r) => r.role_codename === 'company_super_admin');
}

/**
 * The "view-as" rule for the admin app: an internal platform super admin has the
 * full platform surface (all menus, all routes, the company list IA) ONLY while
 * they are not scoped to a specific company. When they select a company in the
 * dashboard switcher (their `active_enterprise_uuid` is set), they "act as" that
 * company's admin everywhere — so this returns `false` and the UI gates on their
 * returned permissions and renders the single-company layout, exactly like a
 * company admin. The switcher itself stays gated on the raw `is_superuser` flag
 * so they can change company or return to "All Companies".
 *
 * Keys off `is_superuser` (platform), not the Company Super Admin role: company
 * admins are always company-scoped and gate on permissions regardless.
 */
export function effectiveIsSuperAdmin(
  user: Pick<AuthUser, 'is_superuser' | 'active_enterprise_uuid'> | null | undefined
): boolean {
  if (!user) return false;
  return Boolean(user.is_superuser) && !user.active_enterprise_uuid;
}

/** Returns `true` if the user is a Company Learner. */
export function isCompanyLearnerUser(user: Pick<AuthUser, 'roles'>): boolean {
  return user.roles.some((r) => r.role_level === 5);
}

/** Returns `true` if the user is an Individual Learner. */
export function isIndividualLearnerUser(user: Pick<AuthUser, 'roles'>): boolean {
  return user.roles.some((r) => r.role_level === 6);
}

/**
 * Returns the display name of the user's primary role: the role held in the
 * active enterprise when one is set, otherwise the highest-privilege
 * (lowest-level) role. Returns `undefined` when the user has no roles.
 */
export function primaryRoleName(
  user: Pick<AuthUser, 'roles' | 'active_enterprise_uuid'>
): string | undefined {
  const roles = user.roles ?? [];
  if (roles.length === 0) return undefined;
  const activeRole = user.active_enterprise_uuid
    ? roles.find((r) => r.enterprise_uuid === user.active_enterprise_uuid)
    : undefined;
  if (activeRole) return activeRole.role_name;
  return [...roles].sort((a, b) => a.role_level - b.role_level)[0].role_name;
}
